import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { date as fmtDate } from "@/lib/format";
import { PageHeader, Pill, Table, Td, Th } from "@/components/ui";
import { popup, confirmPopup } from "@/components/popup";

interface Item {
  id: number;
  platega_id: string;
  user_id: number;
  telegram_id: number;
  username: string | null;
  kind: string;
  tier: string | null;
  period_days: number;
  amount: number;
  status: string;
  next_charge_at: string | null;
  last_charge_at: string | null;
  charges_ok: number;
  charges_failed: number;
  created_at: string | null;
}

interface Response {
  items: Item[];
  total: number;
  by_status: Record<string, number>;
  active_amount: number;
}

const STATUS_LABEL: Record<string, string> = {
  pending: "ждёт привязки",
  active: "активна",
  past_due: "списание не прошло",
  cancelled: "отменена",
  failed: "не активировалась",
};

const FILTERS: { key: string; label: string }[] = [
  { key: "", label: "Все" },
  { key: "active", label: "Активные" },
  { key: "past_due", label: "Сбой списания" },
  { key: "pending", label: "Ждут привязки" },
  { key: "cancelled", label: "Отменённые" },
];

export default function Recurring() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("");

  const q = useQuery<Response>({
    queryKey: ["recurring", status],
    queryFn: () =>
      api(`/api/admin/recurring?limit=200${status ? `&status=${status}` : ""}`),
  });

  const cancel = useMutation({
    mutationFn: (userId: number) =>
      api(`/api/admin/users/${userId}/recurring/cancel`, { method: "POST" }),
    onSuccess: () => {
      popup({ tone: "success", title: "Отключено", body: "Списаний больше не будет." });
      qc.invalidateQueries({ queryKey: ["recurring"] });
    },
    onError: () =>
      popup({ tone: "error", title: "Не вышло", body: "Platega не приняла отмену." }),
  });

  const items = q.data?.items ?? [];
  const by = q.data?.by_status ?? {};

  return (
    <div className="p-4 md:p-8">
      <PageHeader title="Автопродление" />

      <p className="text-zinc-500 text-sm mb-6 max-w-2xl">
        Рекуррентные подписки Platega (СБП). Списания идут на их стороне по
        расписанию — мы принимаем колбэки и продлеваем доступ. Повторных попыток
        при сбое Platega не делает, поэтому «сбой списания» разбирается вручную.
      </p>

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Stat label="Активных" value={by.active ?? 0} />
        <Stat label="За цикл" value={`${(q.data?.active_amount ?? 0).toFixed(0)} ₽`} />
        <Stat label="Сбой списания" value={by.past_due ?? 0} />
        <Stat label="Всего" value={q.data?.total ?? 0} />
      </section>

      <div className="flex gap-2 mb-4 flex-wrap">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setStatus(f.key)}
            className={`px-3 h-8 text-xs font-semibold rounded-lg border transition-colors ${
              status === f.key
                ? "bg-brand/15 text-brand border-brand/30"
                : "text-zinc-400 border-white/[0.07] hover:text-zinc-200"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <Table>
        <thead>
          <tr>
            <Th>Юзер</Th>
            <Th>Продукт</Th>
            <Th>Списание</Th>
            <Th>Статус</Th>
            <Th>Следующее</Th>
            <Th>Списаний</Th>
            <Th>{""}</Th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <tr key={it.id}>
              <Td>
                <Link to={`/users/${it.user_id}`} className="text-zinc-200 hover:text-brand">
                  {it.username ? `@${it.username}` : `id ${it.user_id}`}
                </Link>
                <div className="text-zinc-500 text-xs tabular-nums">{it.telegram_id}</div>
              </Td>
              <Td>
                <Pill>{it.kind === "vpn" ? "VPN" : "Прокси"}</Pill>
              </Td>
              <Td className="tabular-nums">
                {it.amount.toFixed(0)} ₽
                <span className="text-zinc-500 text-xs"> / {it.period_days}д</span>
              </Td>
              <Td>
                <span
                  className={
                    it.status === "active"
                      ? "text-emerald-400"
                      : it.status === "past_due"
                        ? "text-amber-400"
                        : "text-zinc-400"
                  }
                >
                  {STATUS_LABEL[it.status] ?? it.status}
                </span>
              </Td>
              <Td className="text-zinc-400">
                {it.next_charge_at ? fmtDate(it.next_charge_at) : "—"}
              </Td>
              <Td className="tabular-nums text-zinc-400">
                {it.charges_ok}
                {it.charges_failed > 0 && (
                  <span className="text-amber-400"> · {it.charges_failed}</span>
                )}
              </Td>
              <Td>
                {it.status !== "cancelled" && it.status !== "failed" && (
                  <button
                    className="text-zinc-500 hover:text-rose-300 text-sm"
                    onClick={async () => {
                      const ok = await confirmPopup({
                        title: "Отключить автопродление?",
                        body: "Списаний больше не будет. Обратно включить сможет только сам юзер.",
                        confirmLabel: "Отключить",
                        danger: true,
                      });
                      if (ok) cancel.mutate(it.user_id);
                    }}
                  >
                    Отключить
                  </button>
                )}
              </Td>
            </tr>
          ))}
          {!items.length && (
            <tr>
              <td colSpan={7} className="text-zinc-500 py-8 text-center">
                Подписок нет.
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-2xl card-elev p-5">
      <div className="text-[11px] uppercase tracking-[0.14em] text-zinc-500 mb-2">{label}</div>
      <div className="text-3xl font-bold tabular-nums tracking-tight">{value}</div>
    </div>
  );
}
